import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import AdminSidebar from "../shared/AdminSidebar";
import axios from "axios";
import { getNodeApiBase } from "../../../utils/apiBase";
import "./Project_Soumi.css";

const Project_Soumi = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add: search query state
  const [q, setQ] = useState("");
  const nodeApiBase = getNodeApiBase();

  useEffect(() => {
    const fetchDetailedProjects = async () => {
      try {
        // Legacy PHP base (served by Apache); ensure REACT_APP_BASE_URL points to your PHP root (e.g., https://domain/irc/api)
        const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
        const response = await axios.get(
          `${baseUrl}/ProjectSoumi.php?action=getDetailedProjectsWithBudgetParams`
        );
        if (response.data.status === "success") {
          setProjects(response.data.data);
        } else {
          setError(response.data.message);
        }
      } catch (err) {
        console.error("Error fetching project details:", err);
        setError("Failed to fetch project details.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetailedProjects();
  }, []);

  // Helpers for PDF
  const arrayBufferToBase64 = (buf) => {
    let binary = "";
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  };
  const loadImageAsDataUrl = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.width;
        c.height = img.height;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = src;
    });

  // REMOVE font embedding to avoid jsPDF metric crashes stopping the render
  const registerFonts = async (_doc) => {
    return false; // force helvetica usage
  };

  const fmtDate = (d) => {
    if (!d) return "";
    const s = String(d);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return s;
    return `${m[3]}/${m[2]}/${m[1]}`;
  };
  const fmtNumber = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? v ?? "" : n.toLocaleString("fr-FR");
  };

  // Add: display helpers for table cells
  const clean = (v) => {
    if (v === null || v === undefined) return "—";
    const s = String(v).trim();
    if (!s || s === "N/A" || s === "0000-00-00") return "—";
    return s;
  };
  const printDate = (d) => {
    const s = fmtDate(d);
    if (!s || s === "00/00/0000") return "—";
    return s;
  };
  const printMoney = (m) => {
    const n = fmtNumber(m);
    return n && n !== "NaN" ? `${n} MAD` : "—";
  };

  // Helper to get budget appellation for a project
  const getBudgetAppellation = (project) => {
    // Display appellation from budget_parametre if available, otherwise fallback
    return project.appellation || project.budget_appellation || clean(project.valeur_description);
  };

  // Deduplicate projects by idprojet (latest wins)
  const dedupedProjects = useMemo(() => {
    const byId = new Map();
    for (const p of projects) {
      const key = p && p.idprojet != null ? String(p.idprojet) : Math.random().toString(36);
      byId.set(key, p);
    }
    return Array.from(byId.values());
  }, [projects]);

  // Add: filtered list (use deduped list)
  const filteredProjects = dedupedProjects.filter((p) => {
    const term = q.trim().toLowerCase();
    if (!term) return true;
    const fields = [
      p.titre_projet,
      p.chercheur_nom,
      p.Domaine,
      p.nom_equipe,
      p.valeur_description,
      p.valeur_montant,
      p.idprojet,
    ].map((v) => (v == null ? "" : String(v).toLowerCase()));
    return fields.some((f) => f.includes(term));
  });

  // Fetch full details for a project (server JSON)
  const fetchProjectDetails = async (idprojet) => {
    const apiBase = nodeApiBase;
    // Use Node endpoint instead of PHP
    const url = `${apiBase}/api/pdf-details?projectId=${encodeURIComponent(idprojet)}`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`Failed to load details (${res.status})`);
    const data = await res.json();
    if (data.status !== "success") throw new Error(data.message || "Details status != success");
    return data; // { project, budget_projet, equipe, ... }
  };

  // Fallback: fetch projet_evaluation directly from /api/project-evaluation
  const fetchProjectEvaluationsFallback = async (idprojet, idcompetition) => {
    try {
      const apiBase = nodeApiBase;
      const url = `${apiBase}/api/project-evaluation?id_projet=${encodeURIComponent(idprojet)}&id_competition=${encodeURIComponent(idcompetition || '')}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return [];
      const j = await res.json();
      if (j?.status !== 'success' || !Array.isArray(j.data)) return [];
      return j.data;
    } catch {
      return [];
    }
  };

  // Helper: same API base logic used by forum_evaluation.jsx
  const getApiBase = () => nodeApiBase;

  // Fetch related data like forum_evaluation (competition title, project info, related arrays)
  const fetchRelatedForProject = async (idcompetition, idprojet) => {
  const apiBase = getApiBase();
    const out = { competitionTitle: "", projectInfo: null, related: { equipe: [], membres: [], planning: [], taches: [], budget: [] } };
    try {
      if (idcompetition) {
        const r = await fetch(`${apiBase}/api/evaluateur/competitions/${encodeURIComponent(idcompetition)}`);
        const j = await r.json();
        if (j?.status === "success" && j.competition) {
          out.competitionTitle = j.competition.titre || j.competition.libelle || "";
        }
      }
    } catch {}
    try {
      if (idcompetition && idprojet) {
        const pRes = await fetch(`${apiBase}/api/evaluateur/competitions/${encodeURIComponent(idcompetition)}/projets/${encodeURIComponent(idprojet)}`);
        const pj = await pRes.json();
        if (pj?.status === "success") out.projectInfo = pj.projet || null;
      }
    } catch {}
    try {
      if (idprojet) {
        const relRes = await fetch(`${apiBase}/api/evaluateur/projets/${encodeURIComponent(idprojet)}/related`);
        const relJson = await relRes.json();
        if (relJson?.status === "success") {
          out.related = {
            equipe: relJson.equipe || [],
            membres: relJson.membres || [],
            planning: relJson.planning || [],
            taches: relJson.taches || [],
            budget: relJson.budget || [],
          };
        }
      }
    } catch {}
    return out;
  };

  // REMOVE font embedding (keep stable helvetica)

  // Fetch members for a list of team IDs from Node route
  const fetchMembersByEquipeIds = async (ids) => {
    const apiBase = nodeApiBase;
    if (!ids || !ids.length) return [];
    const bulkUrl = `${apiBase}/api/equipes/membres?ids=${encodeURIComponent(ids.join(","))}`;
    try {
      // Bulk request
      const res = await fetch(bulkUrl, { headers: { Accept: "application/json" } });
      let list = [];
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.membres)) list = data.membres;
      }
      // Fallback: per-id if bulk returned empty
      if (!list.length) {
        const perId = await Promise.all(
          ids.map(async (id) => {
            try {
              const r = await fetch(`${apiBase}/api/equipes/${encodeURIComponent(id)}/membres`, {
                headers: { Accept: "application/json" },
              });
              if (!r.ok) return [];
              const j = await r.json();
              return Array.isArray(j.membres) ? j.membres : [];
            } catch {
              return [];
            }
          })
        );
        list = perId.flat();
        // Dedup by id_membre if present
        const seen = new Set();
        list = list.filter((m) => {
          const key = m.id_membre ?? `${m.nom}|${m.prenom}|${m.email}|${m.id_equipe}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }
      console.log("[PDF] members fetch", { teamIds: ids, count: list.length });
      return list;
    } catch (e) {
      console.warn("[PDF] members fetch error", e);
      return [];
    }
  };

  const generateProjectPdf = async (project) => {
    console.log("[PDF] Start client PDF for project:", project?.idprojet);

    // jsPDF dynamic import
    let JsPdfCtor;
    try {
      const mod = await import("jspdf");
      JsPdfCtor = mod.jsPDF || mod.default || mod;
      if (!JsPdfCtor) throw new Error("jsPDF constructor not found");
    } catch (e) {
      console.error("[PDF] Failed to load jsPDF:", e);
      alert("Erreur: jsPDF n'a pas pu être chargé.");
      throw e;
    }

    const doc = new JsPdfCtor({ unit: "pt", format: "a4" });
    await registerFonts(doc);
    doc.setFont("helvetica", "normal");
    const safeText = (s) => {
      const str = s == null ? "" : String(s);
      // replace narrow/nbsp spaces that WinAnsi can't render
      const noNbsp = str.replace(/[\u00A0\u202F]/g, " ");
      // strip diacritics for Helvetica WinAnsi
      return noNbsp.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    };

    // Predeclare all dataset arrays to avoid no-undef further down
    let budgetParams = [];
    let planningRows = [];
    let tacheRows = [];
    let comiteRows = [];
  let evaluationRows = [];
    let criteresRows = [];
    let sousCritRows = [];
  let projetEvalRows = [];

    // 1) Server JSON (Node) for base: project + related arrays
    let server = { project: project, budget: [], team: [] };
    try {
      const full = await fetchProjectDetails(project.idprojet);
      if (full?.status === "success") {
        server = {
          ...full,
          project: full.project || project,
          budget: Array.isArray(full.budget) ? full.budget : (Array.isArray(full.budget_projet) ? full.budget_projet : []),
          team: Array.isArray(full.team) ? full.team : (Array.isArray(full.equipe) ? full.equipe : []),
        };
      }
    } catch (e) {
      console.warn("[PDF] Server JSON failed, will rely on related/list fallbacks.", e);
    }
    const details = server;

    // 2) Related data like forum_evaluation
    const idcompetition = project?.idcompetition || server?.project?.idcompetition || "";
    const rel = await fetchRelatedForProject(idcompetition, project.idprojet);

    // Compose a rich project object
    const p = {
      ...(server.project || {}),
      ...(rel.projectInfo || {}),
      // ensure essentials
      idprojet: (server.project?.idprojet || rel.projectInfo?.idprojet || project.idprojet),
      titre_projet: server.project?.titre_projet || rel.projectInfo?.titre_projet || project.titre_projet,
      Domaine: server.project?.Domaine || rel.projectInfo?.Domaine || project.Domaine,
      Sous_Domaine: server.project?.Sous_Domaine || rel.projectInfo?.sous_domaine || rel.projectInfo?.Sous_Domaine || "",
      Montant_global: server.project?.Montant_global || rel.projectInfo?.Montant_global || "",
      Montant_subvention: server.project?.Montant_subvention || rel.projectInfo?.Montant_subvention || "",
      Resume_francais: server.project?.Resume_francais || rel.projectInfo?.Resume_francais,
      Resume_anglais: server.project?.Resume_anglais || rel.projectInfo?.Resume_anglais,
      Introduction_projet: server.project?.Introduction_projet || rel.projectInfo?.Introduction_projet,
      Objectif_general_specifiques: server.project?.Objectif_general_specifiques || rel.projectInfo?.Objectifs || rel.projectInfo?.Objectif_general_specifiques,
      Methodologie_proposee: server.project?.Methodologie_proposee || rel.projectInfo?.Methodologie_proposee,
      resultats_attendus: server.project?.resultats_attendus || rel.projectInfo?.Resultats_attendus || rel.projectInfo?.resultats_attendus,
      considerations_ethiques: server.project?.considerations_ethiques || rel.projectInfo?.considerations_ethiques,
      date_debut_projet: server.project?.date_debut_projet || rel.projectInfo?.date_debut_projet,
      date_fin_projet: server.project?.date_fin_projet || rel.projectInfo?.date_fin_projet,
      chercheur_nom: project.chercheur_nom || server.project?.chercheur_nom || "",
      nom_equipe: project.nom_equipe || server.project?.nom_equipe || "",
      idcompetition: idcompetition,
    };

    // Compose rows
    let budgets = [];
    if (Array.isArray(rel.related?.budget) && rel.related.budget.length) {
      budgets = rel.related.budget;
    } else if (Array.isArray(server.budget) && server.budget.length) {
      budgets = server.budget; // normalized from budget_projet
    } else if (project?.valeur_description || project?.valeur_montant) {
      budgets = [{ valeur_description: project.valeur_description, valeur_montant: project.valeur_montant }];
    }

    let equipes = [];
    if (Array.isArray(rel.related?.equipe) && rel.related.equipe.length) {
      equipes = rel.related.equipe.map((e) => ({
        nom_equipe: e.nom_equipe || e.nom || (server.project?.nom_equipe || "—"),
        idchercheur: e.idchercheur || server.project?.idchercheur || "—",
        idevaluteur: e.idevaluteur || server.project?.idevaluteur || "—",
      }));
    } else if (Array.isArray(server.team) && server.team.length) {
      equipes = server.team; // normalized from equipe
    } else if ((server.project?.nom_equipe) || (server.project?.idchercheur) || (server.project?.idevaluteur)) {
      equipes = [{
        nom_equipe: server.project?.nom_equipe || "—",
        idchercheur: server.project?.idchercheur || "—",
        idevaluteur: server.project?.idevaluteur || "—",
      }];
    }

    // Fetch members strictly by team IDs; fallback to details.membres if API returns empty
    const teamIds = Array.from(
      new Set(
        (Array.isArray(equipes) ? equipes : [])
          .map((t) => t && t.id_equipe)
          .concat((Array.isArray(server.team) ? server.team : []).map((t) => t && t.id_equipe))
          .filter((v) => v !== undefined && v !== null && v !== "")
      )
    );
    let membresRows = [];
    if (teamIds.length) {
      try {
        membresRows = await fetchMembersByEquipeIds(teamIds);
      } catch {}
    }
    if (!membresRows.length && Array.isArray(details.membres)) {
      membresRows = details.membres;
    }

    // Debug what the API returned (first few)
    if (membresRows.length) {
      console.log("[PDF] membres sample", membresRows.slice(0, 5));
    }

    // Collect enriched arrays from backend (assign to predeclared vars)
    budgetParams = Array.isArray(details.budget_parametre) ? details.budget_parametre : [];
    planningRows = Array.isArray(details.planning_full)
      ? details.planning_full
      : (Array.isArray(details.planning) ? details.planning : []);
    tacheRows = Array.isArray(details.tache)
      ? details.tache
      : (Array.isArray(details.taches) ? details.taches : []);
    comiteRows = Array.isArray(details.comite) ? details.comite : [];
    // NEW: normalize evaluation/criteres/sous_criteres keys defensively
    const norm = (arr, map) =>
      (Array.isArray(arr) ? arr : []).map((row) => {
        const out = { ...row };
        Object.entries(map).forEach(([target, candidates]) => {
          if (out[target] == null) {
            for (const key of candidates) {
              if (out[key] != null) {
                out[target] = out[key];
                break;
              }
            }
          }
        });
        return out;
      });
    evaluationRows = Array.isArray(details.evaluation)
      ? details.evaluation
      : (Array.isArray(details.Evaluation) ? details.Evaluation : []);
    criteresRows = norm(
      Array.isArray(details.criteres) ? details.criteres : (Array.isArray(details.criteria) ? details.criteria : []),
      { ID_criteres: ['ID_critere', 'id_criteres', 'id_critere'], Titre_criteres: ['Titre_critere', 'titre', 'libelle'] }
    );
    sousCritRows = norm(
      Array.isArray(details.sous_criteres) ? details.sous_criteres : (Array.isArray(details.sub_criteria) ? details.sub_criteria : []),
      {
        ID_sous_critres: ['ID_sous_criteres', 'id_sous_critres', 'id_sous_critere'],
        Titre_sous_critres: ['Titre_sous_criteres', 'titre_sous_critres', 'libelle'],
        ID_criteres: ['id_critere', 'ID_critere', 'id_criteres'],
        note: ['Note', 'note_sous_critere'],
      }
    );

    // NEW: Projet evaluation normalize
    projetEvalRows = norm(
      Array.isArray(details.projet_evaluation) ? details.projet_evaluation : [],
      {
        idevaluateur: ['idevaluateur', 'idevaluteur', 'id_evaluateur'],
        commentaire: ['commentaire', 'comment'],
        note: ['note', 'Note'],
        id_critere: ['id_critere', 'ID_critere', 'ID_criteres', 'id_criteres', 'idcritere', 'id_crit'],
        id_sous_critere: ['id_sous_critere', 'ID_sous_critere', 'ID_sous_criteres', 'id_sous_critres', 'id_sous_criteres', 'idsouscritere', 'id_souscritere'],
      }
    );
    if (projetEvalRows.length) {
      console.log('[PDF] projet_evaluation sample', projetEvalRows.slice(0, 5));
    }

    // Fallback path: if no evaluator rows came from pdf-details, fetch directly
    if (!projetEvalRows.length && p.idprojet && p.idcompetition) {
      const fallbackRows = await fetchProjectEvaluationsFallback(p.idprojet, p.idcompetition);
      if (fallbackRows && fallbackRows.length) {
        projetEvalRows = norm(fallbackRows, {
          idevaluateur: ['idevaluateur', 'idevaluteur', 'id_evaluateur'],
          commentaire: ['commentaire', 'comment'],
          note: ['note', 'Note'],
          id_critere: ['id_critere', 'ID_critere', 'ID_criteres', 'id_criteres', 'idcritere', 'id_crit'],
          id_sous_critere: ['id_sous_critere', 'ID_sous_critere', 'ID_sous_criteres', 'id_sous_critres', 'id_sous_criteres', 'idsouscritere', 'id_souscritere'],
        });
        console.log('[PDF] fallback projet_evaluation sample', projetEvalRows.slice(0, 5));
      }
    }

    // NEW: Évaluateurs from backend (normalized keys)
    const evaluateursRows = Array.isArray(details.evaluateurs) ? details.evaluateurs : [];

    // Debug payload sizes
    console.log("[PDF] payload sizes", {
      project: server.project ? 1 : 0,
      budget: Array.isArray(server.budget) ? server.budget.length : 0,
      team: Array.isArray(server.team) ? server.team.length : 0,
      members: membresRows.length,
      budget_parametre: budgetParams.length,
      planning: planningRows.length,
      tache: tacheRows.length,
      comite: comiteRows.length,
      evaluation: evaluationRows.length,
      criteres: criteresRows.length,
      sous_criteres: sousCritRows.length,
      evaluateurs: evaluateursRows.length,
      projet_evaluation: projetEvalRows.length,
    });

    // Layout tokens (same spirit as forum_evaluation)
    const PAGE_W = doc.internal.pageSize.getWidth();
    const PAGE_H = doc.internal.pageSize.getHeight();
    const MARGIN_X = 46;
    const CONTENT_W = PAGE_W - MARGIN_X * 2;
    const HEADER_H = 60;
    const ACCENT = { r: 41, g: 98, b: 255 };
    const LIGHT_BG = [245, 247, 250];
    const BORDER_CLR = [214, 219, 223];
    let y = HEADER_H + 26;

    // Utilities
    const ensureSpace = (needed = 100) => {
      if (y + needed > PAGE_H - 70) {
        doc.addPage();
        drawHeader();
        y = HEADER_H + 26;
      }
    };
    const sectionBreak = (() => {
      const TOP_Y = HEADER_H + 26;
      const GAP = 24;
      return () => {
        if (y > TOP_Y) {
          y += GAP;
          ensureSpace(0);
        }
      };
    })();
    const fmtNumberLocal = (v) => {
      if (v == null || v === "") return "";
      const n = Number(String(v).toString().replace(/[^0-9.-]/g, ""));
      return isNaN(n) ? String(v) : n.toLocaleString("fr-FR");
    };
    const formatDate = (val) => {
      if (!val) return "";
      const s = String(val);
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return s;
      return `${m[3]}/${m[2]}/${m[1]}`;
    };

    // Header/watermark
    const drawHeader = () => {
      // Banner
      doc.setDrawColor(225);
      doc.setLineWidth(0.8);
      doc.setFont("helvetica", "bold");
      // Logo (if available)
      try {
        // use existing image element import path; we won't block on failure
        // You already have loadImageAsDataUrl above in this file
      } catch {}
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      const compTitle = rel.competitionTitle || "Compétition";
      doc.text(safeText(compTitle), MARGIN_X, 26, { maxWidth: CONTENT_W - 100 });
      // Compute name values once
      const guessNamesFromFull = (full) => {
        const parts = String(full || '').trim().split(/\s+/);
        if (!parts.length) return { prenom: '', nom: '' };
        if (parts.length === 1) return { prenom: parts[0], nom: '' };
        return { prenom: parts[0], nom: parts.slice(1).join(' ') };
      };
      const fromFull = guessNamesFromFull(p.chercheur_nom || '');
      const prenomVal = (p.prenom || fromFull.prenom || '—').trim();
      const nomVal = (p.nom || fromFull.nom || '—').trim();
      // Left second line: Project Title
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      const projTitle = p.titre_projet || `Projet #${p.idprojet}`;
      doc.text(safeText(projTitle), MARGIN_X, 42, { maxWidth: CONTENT_W - 250 });
      // Right side: Nom/Prénom aligned to the right
      const rightX = PAGE_W - MARGIN_X;
      const nameLine = `Candidat: ${nomVal} ${prenomVal}`;
      doc.text(safeText(nameLine), rightX, 26, { align: 'right', maxWidth: CONTENT_W / 2 });
      doc.line(MARGIN_X, HEADER_H, PAGE_W - MARGIN_X, HEADER_H);
    };
    const addFooter = () => {
      const pages = doc.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setDrawColor(230);
        doc.setLineWidth(0.6);
        doc.line(MARGIN_X, PAGE_H - 54, PAGE_W - MARGIN_X, PAGE_H - 54);
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(`Page ${i} / ${pages}`, PAGE_W - MARGIN_X, PAGE_H - 34, { align: "right" });
      }
    };
    const sectionHeader = (title) => {
      sectionBreak();
      ensureSpace(60);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12.5);
      doc.setTextColor(ACCENT.r, ACCENT.g, ACCENT.b);
      doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b);
      doc.rect(MARGIN_X, y - 10, 6, 28, "F");
      doc.setDrawColor(ACCENT.r, ACCENT.g, ACCENT.b);
      doc.roundedRect(MARGIN_X + 10, y - 12, CONTENT_W - 16, 32, 6, 6, "S");
      doc.text(safeText(title.toUpperCase()), MARGIN_X + 24, y + 4);
      y += 32;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.8);
      doc.setTextColor(40, 40, 40);
    };
    // Secondary header for nested sections (e.g., Critères under Évaluation)
    const subSectionHeader = (title) => {
      sectionBreak();
      ensureSpace(30);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11.2);
      doc.setTextColor(ACCENT.r, ACCENT.g, ACCENT.b);
      doc.text(safeText(title.toUpperCase()), MARGIN_X + 12, y + 2);
      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.6);
      doc.setTextColor(40, 40, 40);
    };
    const paragraph = (text) => {
      if (!text) return;
      const lines = doc.splitTextToSize(safeText(text), CONTENT_W - 24);
      const lineH = 14;
      const height = lines.length * lineH + 16;
      ensureSpace(height + 10);
      doc.setFillColor(...LIGHT_BG);
      doc.setDrawColor(...BORDER_CLR);
      doc.roundedRect(MARGIN_X, y, CONTENT_W, height, 6, 6, "F");
      doc.roundedRect(MARGIN_X, y, CONTENT_W, height, 6, 6, "S");
      let innerY = y + 12;
      doc.setFontSize(10.5);
      lines.forEach((ln) => {
        doc.text(ln, MARGIN_X + 12, innerY);
        innerY += lineH;
      });
      y += height + 10;
      doc.setFontSize(10.8);
    };
    const metaGrid = (pairs) => {
      const filtered = pairs.filter((p) => p[1] !== undefined && p[1] !== null && String(p[1]).trim() !== "");
      if (!filtered.length) return;
      const COLS = 2;
      const colWidth = CONTENT_W / COLS;
      const processed = filtered.map(([label, value]) => {
        let val = Array.isArray(value) ? value.join(", ") : String(value);
        if (/^\d{4}-\d{2}-\d{2}/.test(val)) val = formatDate(val);
        const lines = doc.splitTextToSize(safeText(val), colWidth - 28);
        return { label, lines, height: lines.length * 12 + 4 };
      });
      // Render with support for full-width rows (e.g., very long 'Sous Domaine')
      const shouldFullWidth = (cell) => {
        const label = String(cell.label || '').toLowerCase();
        return label.includes('sous domaine') || cell.lines.length > 2;
      };

      // First compute total height with potential full-width rows
      let measureY = 0;
      for (let i = 0; i < processed.length; ) {
        const cell = processed[i];
        if (shouldFullWidth(cell)) {
          const blockH = cell.height + 12;
          measureY += blockH;
          i += 1;
        } else {
          const left = processed[i];
          const right = processed[i + 1];
          const rowH = Math.max(left ? left.height : 0, right ? right.height : 0) + 12;
          measureY += rowH;
          i += 2;
        }
      }
      const totalHeight = measureY + 12;
      ensureSpace(totalHeight + 10);
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...BORDER_CLR);
      doc.roundedRect(MARGIN_X, y, CONTENT_W, totalHeight, 8, 8, "F");
      doc.roundedRect(MARGIN_X, y, CONTENT_W, totalHeight, 8, 8, "S");
      let cursorY = y + 16;

      for (let i = 0; i < processed.length; ) {
        const cell = processed[i];
        if (shouldFullWidth(cell)) {
          // Full-width row with label/value spanning both columns
          const baseX = MARGIN_X + 14;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10.2);
          doc.setTextColor(70, 70, 70);
          const label = safeText(`${cell.label}:`);
          doc.text(label, baseX, cursorY);
          const labelWidth = doc.getTextWidth(label + " ");
          doc.setFont("helvetica", "normal");
          doc.setTextColor(30, 30, 30);
          const valueLines = doc.splitTextToSize(
            safeText(cell.lines.join(' ')),
            CONTENT_W - 28 - labelWidth
          );
          valueLines.forEach((ln, idx) => doc.text(ln, baseX + labelWidth, cursorY + idx * 12));
          cursorY += Math.max(16, valueLines.length * 12 + 4) + 12;
          i += 1;
        } else {
          const left = processed[i];
          const right = processed[i + 1];
          const rowH = Math.max(left ? left.height : 0, right ? right.height : 0) + 12;
          // Left cell
          if (left) {
            const baseX = MARGIN_X + 14;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10.2);
            doc.setTextColor(70, 70, 70);
            const label = safeText(`${left.label}:`);
            doc.text(label, baseX, cursorY);
            const labelWidth = doc.getTextWidth(label + " ");
            doc.setFont("helvetica", "normal");
            doc.setTextColor(30, 30, 30);
            left.lines.forEach((ln, idx) => doc.text(ln, baseX + labelWidth, cursorY + idx * 12));
          }
          // Right cell
          if (right) {
            const baseX = MARGIN_X + colWidth + 14;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10.2);
            doc.setTextColor(70, 70, 70);
            const label = safeText(`${right.label}:`);
            doc.text(label, baseX, cursorY);
            const labelWidth = doc.getTextWidth(label + " ");
            doc.setFont("helvetica", "normal");
            doc.setTextColor(30, 30, 30);
            right.lines.forEach((ln, idx) => doc.text(ln, baseX + labelWidth, cursorY + idx * 12));
          }
          cursorY += rowH;
          i += 2;
        }
      }

      y += totalHeight + 14;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(10.8);
    };
    const drawBudgetTable = (rows) => {
      if (!rows || !rows.length) return;
      ensureSpace(80);
      const headers = ["Description", "Montant (MAD)"];
      const colWidths = [CONTENT_W * 0.68, CONTENT_W * 0.32];
      const rowH = 22;
      doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.rect(MARGIN_X, y, CONTENT_W, rowH, "F");
      let x = MARGIN_X + 10;
      doc.text(headers[0], x, y + 14, { maxWidth: colWidths[0] - 16 });
      x = MARGIN_X + colWidths[0] + 10;
      doc.text(headers[1], x, y + 14);
      y += rowH;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.2);
      doc.setTextColor(30, 30, 30);
      rows.forEach((r, idx) => {
        ensureSpace(rowH + 10);
        const desc = r.valeur_description || r.description || r.Description || "-";
        const amtVal = r.valeur_montant ?? r.montant ?? r.Montant ?? r.amount ?? "";
        const amt = fmtNumberLocal(amtVal);
        doc.setFillColor(idx % 2 ? 249 : 255, 250, 252);
        doc.rect(MARGIN_X, y, CONTENT_W, rowH, "F");
        doc.setDrawColor(240);
        doc.rect(MARGIN_X, y, CONTENT_W, rowH, "S");
        doc.text(doc.splitTextToSize(safeText(desc), colWidths[0] - 20), MARGIN_X + 10, y + 14);
        doc.text(safeText(String(amt || "-")), MARGIN_X + colWidths[0] + 10, y + 14);
        y += rowH;
      });
      ensureSpace(rowH + 10);
      const total = rows.reduce((s, r) => s + (parseFloat(r.valeur_montant ?? r.montant ?? 0) || 0), 0);
      doc.setFont("helvetica", "bold");
      doc.setFillColor(240, 240, 240);
      doc.rect(MARGIN_X, y, CONTENT_W, rowH, "F");
      doc.rect(MARGIN_X, y, CONTENT_W, rowH, "S");
      doc.text("TOTAL", MARGIN_X + 10, y + 14);
      doc.text(safeText(fmtNumberLocal(total)), MARGIN_X + colWidths[0] + 10, y + 14);
      y += rowH + 14;
      doc.setFont("helvetica", "normal");
    };

    // Table: Évaluation (Critères / Sous-critères / Notes)
    const drawEvaluationTable = (criteres, sousCrit, evalRows) => {
      if (!criteres.length && !sousCrit.length) return;
      ensureSpace(80);

      // Pre-map sous-criteres by ID_criteres
      const byCrit = new Map();
      sousCrit.forEach((sc) => {
        const cid = sc.ID_criteres != null ? String(sc.ID_criteres) : "";
        if (!byCrit.has(cid)) byCrit.set(cid, []);
        byCrit.get(cid).push(sc);
      });
      // Map evaluator rows by exact sous-critere id when provided
      const evalForSous = new Map();
      (evalRows || []).forEach((row) => {
        const sid = row.id_sous_critere != null ? String(row.id_sous_critere) : "";
        if (!sid) return;
        const list = evalForSous.get(sid) || [];
        list.push(row);
        evalForSous.set(sid, list);
      });

      // Compute totals
      const toNum = (v) => {
        const n = parseFloat(String(v).toString().replace(/[^0-9.-]/g, ""));
        return isNaN(n) ? 0 : n;
      };
  const adminTotal = sousCrit.reduce((s, r) => s + toNum(r.note), 0);
  const evaluatorTotal = (evalRows || []).reduce((s, r) => s + toNum(r.note), 0);

      // Header: always 3 columns (remove 4th column)
      const headers = ["Critère", "Sous-critère", "Note"];
      const colW = [CONTENT_W * 0.35, CONTENT_W * 0.53, CONTENT_W * 0.12];
      const rowH = 22;

      // Draw header row
      doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.rect(MARGIN_X, y, CONTENT_W, rowH, "F");
      let cx = MARGIN_X + 10;
      headers.forEach((h, i) => {
        doc.text(h, cx, y + 14);
        cx += colW[i];
      });
      y += rowH;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.2);
      doc.setTextColor(30, 30, 30);

      // Rows by critère
      criteres.forEach((cr, cidx) => {
        const labelCrit = cr.Titre_criteres || cr.Titre_critere || "-";
        const list = byCrit.get(String(cr.ID_criteres)) || [];
        if (!list.length) {
          ensureSpace(rowH + 6);
          doc.setFillColor(cidx % 2 ? 249 : 255, 250, 252);
          doc.rect(MARGIN_X, y, CONTENT_W, rowH, "F");
          doc.setDrawColor(240);
          doc.rect(MARGIN_X, y, CONTENT_W, rowH, "S");
          let x = MARGIN_X + 10;
          doc.text(safeText(labelCrit), x, y + 14, { maxWidth: colW[0] - 16 });
          x += colW[0];
          // sous-critère column is empty since there is no sous-critère under this critère
          doc.text("—", x + 2, y + 14);
          x += colW[1];
          // admin note placeholder
          doc.text("—", x + 2, y + 14);
          y += rowH;
        } else {
          list.forEach((sc, sidx) => {
            ensureSpace(rowH + 6);
            const zebra = (cidx + sidx) % 2 ? 248 : 255;
            doc.setFillColor(zebra, 250, 252);
            doc.rect(MARGIN_X, y, CONTENT_W, rowH, "F");
            doc.setDrawColor(240);
            doc.rect(MARGIN_X, y, CONTENT_W, rowH, "S");

            let x = MARGIN_X + 10;
            // Critère name only on the first row of its group
            if (sidx === 0) {
              doc.text(safeText(labelCrit), x, y + 14, { maxWidth: colW[0] - 16 });
            }
            x += colW[0];

            const title = sc.Titre_sous_critres || sc.Titre_sous_criteres || sc.titre_sous_critres || sc.titre_sous_criteres || "-";
            doc.text(doc.splitTextToSize(safeText(title), colW[1] - 16), x + 2, y + 14);
            x += colW[1];

            const adminNote = toNum(sc.note);
            doc.text(String(adminNote || "-"), x + 2, y + 14);

            y += rowH;
          });
        }
      });

      // Structured summary box under the table (instead of a totals row)
      const drawEvalSummary = () => {
        const boxH = 72;
        ensureSpace(boxH + 10);
        doc.setDrawColor(...BORDER_CLR);
        doc.setFillColor(250, 251, 252);
        doc.roundedRect(MARGIN_X, y, CONTENT_W, boxH, 8, 8, "F");
        doc.roundedRect(MARGIN_X, y, CONTENT_W, boxH, 8, 8, "S");
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(11);
        doc.text("Synthèse des notes", MARGIN_X + 12, y + 18);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(31, 41, 55);
        const pct = adminTotal > 0 ? Math.round((evaluatorTotal / adminTotal) * 100) : 0;
        const lines = evaluatorTotal > 0
          ? [
              `Note maximale (Admin): ${adminTotal}`,
              `Total des notes (Éval): ${evaluatorTotal}`,
              `Ratio: ${evaluatorTotal}/${adminTotal}  (${pct}%)`,
            ]
          : [
              `Note maximale (Admin): ${adminTotal}`,
            ];
        let ly = y + 40;
        lines.forEach((ln) => {
          doc.text(ln, MARGIN_X + 16, ly);
          ly += 14;
        });
        y += boxH + 14;
      };

      drawEvalSummary();
    };

    // Table: Évaluateurs total notes
    const drawEvaluateursTable = (evaluateurs, evalRows, adminMax) => {
      if (!evaluateurs.length && !evalRows.length) return;
      ensureSpace(80);
      const toNum = (v) => {
        const n = parseFloat(String(v).toString().replace(/[^0-9.-]/g, ""));
        return isNaN(n) ? 0 : n;
      };
      // Group by evaluator id
      const groups = new Map();
      evalRows.forEach((r) => {
        const id = r.idevaluateur != null ? String(r.idevaluateur) : "";
        if (!id) return;
        const list = groups.get(id) || [];
        list.push(r);
        groups.set(id, list);
      });

      const headers = ["Évaluateur", "Total", "Max", "Ratio"];
      const colW = [CONTENT_W * 0.6, CONTENT_W * 0.12, CONTENT_W * 0.12, CONTENT_W * 0.16];
      const rowH = 22;

      // Header row
      doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.rect(MARGIN_X, y, CONTENT_W, rowH, "F");
      let x = MARGIN_X + 10;
      headers.forEach((h, i) => {
        doc.text(h, x, y + 14);
        x += colW[i];
      });
      y += rowH;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.2);
      doc.setTextColor(30, 30, 30);

      const adminMaxVal = toNum(adminMax);

      const evalById = new Map(evaluateurs.map((e) => [String(e.idevaluteur ?? e.idevaluateur ?? e.id_evaluateur ?? ""), e]));
      const allIds = Array.from(new Set([...evalById.keys(), ...groups.keys()].filter(Boolean)));
      allIds.forEach((id, idx) => {
        ensureSpace(rowH + 6);
        const zebra = idx % 2 ? 248 : 255;
        doc.setFillColor(zebra, 250, 252);
        doc.rect(MARGIN_X, y, CONTENT_W, rowH, "F");
        doc.setDrawColor(240);
        doc.rect(MARGIN_X, y, CONTENT_W, rowH, "S");

        let cx = MARGIN_X + 10;
        const evalInfo = evalById.get(id) || {};
        const name = (evalInfo.prenom || "").trim() || `#${id}`;
        doc.text(safeText(name), cx, y + 14, { maxWidth: colW[0] - 16 });
        cx += colW[0];

        const list = groups.get(id) || [];
        const total = list.reduce((s, r) => s + toNum(r.note), 0);
        doc.text(String(total), cx + 2, y + 14);
        cx += colW[1];

        doc.text(String(adminMaxVal), cx + 2, y + 14);
        cx += colW[2];

        doc.text(`${total}/${adminMaxVal}`, cx + 2, y + 14);

        y += rowH;
      });

      y += 10;
    };

    // Table: Évaluations détaillées (Évaluateur + Critère + Sous-critère + Commentaire + Note)
    const drawEvaluatorDetailTable = (evaluateurs, evalRows, criteres, sousCrit) => {
      if (!evalRows.length) return;
      ensureSpace(80);

      // We'll use the admin total (sum of all sous-critère notes) as the denominator
      const toNum = (v) => {
        const n = parseFloat(String(v).toString().replace(/[^0-9.-]/g, ""));
        return isNaN(n) ? 0 : n;
      };
      const adminMax = sousCrit.reduce((s, sc) => s + toNum(sc.note), 0);

      // Maps for name lookup and sous-critère → critère
      const evalById = new Map(evaluateurs.map((e) => [String(e.idevaluteur ?? e.idevaluateur ?? e.id_evaluateur ?? ''), e]));
      const critById = new Map(criteres.map((c) => [String(c.ID_criteres), (c.Titre_criteres || c.Titre_critere || '-') ]));
      const sousToCrit = new Map(sousCrit.map((sc) => [String(sc.ID_sous_critres || sc.ID_sous_criteres || sc.id_sous_critres || sc.id_sous_critere || ''), String(sc.ID_criteres ?? sc.id_critere ?? sc.id_criteres ?? '')]));

      // Fuzzy helper used only as a last resort when ids/titles are missing
      const normalize = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const sousIndex = (sousCrit || []).map((sc) => {
        const id = String(sc.ID_sous_critres || sc.ID_sous_criteres || sc.id_sous_critres || sc.id_sous_critere || '');
        const title = sc.Titre_sous_critres || sc.Titre_sous_criteres || sc.titre_sous_critres || sc.titre_sous_criteres || '';
        return { id, title, key: normalize(title) };
      });
      const toTokens = (s) => normalize(s).split(/[^a-z0-9]+/).filter(Boolean);
      const jaccard = (a, b) => {
        const A = new Set(a);
        const B = new Set(b);
        const inter = new Set([...A].filter((x) => B.has(x)));
        const union = new Set([...A, ...B]);
        return union.size ? inter.size / union.size : 0;
      };
      const findSousByComment = (txt) => {
        const key = normalize(txt);
        if (!key) return null;
        // simple contains check first
        let hit = sousIndex.find((s) => s.key && key.includes(s.key));
        if (hit) return hit;
        // try reverse contains (title contains comment phrase)
        hit = sousIndex.find((s) => s.key && s.key.includes(key));
        if (hit) return hit;
        // token-based similarity (Jaccard)
        const toks = toTokens(txt);
        let best = null;
        let bestScore = 0;
        for (const s of sousIndex) {
          const score = jaccard(toks, toTokens(s.title));
          if (score > bestScore) {
            best = s;
            bestScore = score;
          }
        }
        return bestScore >= 0.2 ? best : null; // threshold to avoid random matches
      };

      // Build rows from evalRows
      const rows = evalRows.map((r) => {
        const eid = String(r.idevaluateur ?? '');
        const e = evalById.get(eid) || {};
        const name = (e.prenom || '').trim() || `#${eid}`;
        const sousId = r.id_sous_critere != null ? String(r.id_sous_critere) : '';
        const sousRow = sousId ? sousCrit.find((sc) => String(sc.ID_sous_critres || sc.ID_sous_criteres || sc.id_sous_critres || sc.id_sous_critere || '') === sousId) : null;
        const sousTitleFromApi = r.sous_critere_title || r.titre_sous_criteres || r.Titre_sous_criteres || '';
        let sousTitle = sousTitleFromApi || (sousRow ? (sousRow.Titre_sous_critres || sousRow.Titre_sous_criteres || sousRow.titre_sous_critres || sousRow.titre_sous_criteres || '') : '');
        let critId = r.id_critere != null ? String(r.id_critere) : (sousId ? (sousToCrit.get(String(sousId)) || '') : '');
        // Fallback: fuzzy match if still empty
        if (!sousTitle) {
          const guess = findSousByComment(r.commentaire || '');
          if (guess) {
            sousTitle = guess.title;
            if (!critId) critId = sousToCrit.get(String(guess.id)) || '';
          }
        }
        const critTitleFromApi = r.critere_title || r.titre_critere || r.Titre_critere || r.Titre_criteres || '';
        let critTitle = critTitleFromApi || (critById.get(String(critId)) || '');
        if (!critTitle && sousTitle) {
          // if we got a sous-critère title via fuzzy, derive its critère title via mapping
          const guess = findSousByComment(sousTitle);
          if (guess) {
            const derivedCritId = sousToCrit.get(String(guess.id));
            if (derivedCritId) critTitle = critById.get(String(derivedCritId)) || '';
          }
        }
        const note = r.note ?? r.Note ?? '';
        // Admin note for this sous-critère (from grid)
        let adminNote = '';
        const sidForAdmin = sousId || '';
        if (sidForAdmin) {
          const adminRow = sousCrit.find((sc) => String(sc.ID_sous_critres || sc.ID_sous_criteres || sc.id_sous_critres || sc.id_sous_critere || '') === sidForAdmin);
          if (adminRow && (adminRow.note !== undefined && adminRow.note !== null)) adminNote = String(adminRow.note);
        }
        const comment = r.commentaire || '';
        return { name, crit: critTitle, sous: sousTitle, comment, note, adminNote, sousId };
      });

      // Header
  const headers = ["Évaluateur", "Critère", "Sous-critère", "Commentaire", "Note"];
      const colW = [CONTENT_W * 0.16, CONTENT_W * 0.2, CONTENT_W * 0.28, CONTENT_W * 0.28, CONTENT_W * 0.08];
      const rowPad = 10; // internal padding for multi-line

      doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.rect(MARGIN_X, y, CONTENT_W, 22, "F");
      let hx = MARGIN_X + 10;
      headers.forEach((h, i) => { doc.text(h, hx, y + 14); hx += colW[i]; });
      y += 22;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.2);
      doc.setTextColor(30, 30, 30);

      rows.forEach((r, idx) => {
        // Measure cell heights
        const c1 = doc.splitTextToSize(safeText(r.name || '—'), colW[0] - 16);
        const c2 = doc.splitTextToSize(safeText(r.crit || '—'), colW[1] - 16);
        const c3 = doc.splitTextToSize(safeText(r.sous || '—'), colW[2] - 16);
        const c4 = doc.splitTextToSize(safeText(r.comment || '—'), colW[3] - 16);
        // Show evaluator note over the admin total (e.g., 16/82)
        const c5 = [
          safeText(
            String((r.note != null ? r.note : (r.Note != null ? r.Note : '—')) + '/' + (adminMax || '—'))
          ),
        ];
        const lineH = 12;
        const blockH = Math.max(c1.length, c2.length, c3.length, c4.length, c5.length) * lineH + rowPad;
        ensureSpace(blockH + 6);

        const zebra = idx % 2 ? 248 : 255;
        doc.setFillColor(zebra, 250, 252);
        doc.rect(MARGIN_X, y, CONTENT_W, blockH, "F");
        doc.setDrawColor(240);
        doc.rect(MARGIN_X, y, CONTENT_W, blockH, "S");

        let x = MARGIN_X + 10;
        const writeCol = (lines, width) => {
          lines.forEach((ln, i) => doc.text(ln, x, y + 14 + i * lineH));
          x += width;
        };
        writeCol(c1, colW[0]);
        writeCol(c2, colW[1]);
        writeCol(c3, colW[2]);
        writeCol(c4, colW[3]);
        writeCol(c5, colW[4]);

        y += blockH;
      });

      y += 6;

      // Totals box directly under the detailed table
      const evalTotal = rows.reduce((s, r) => s + toNum(r.note), 0);
      const totalBoxH = 52;
      ensureSpace(totalBoxH + 8);
      doc.setDrawColor(...BORDER_CLR);
      doc.setFillColor(250, 251, 252);
      doc.roundedRect(MARGIN_X, y, CONTENT_W, totalBoxH, 8, 8, "F");
      doc.roundedRect(MARGIN_X, y, CONTENT_W, totalBoxH, 8, 8, "S");
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(11);
      doc.text("Total", MARGIN_X + 12, y + 18);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(31, 41, 55);
      doc.text(`Total (Éval/Admin): ${evalTotal}/${adminMax}`, MARGIN_X + 16, y + 38);
      y += totalBoxH + 10;

      // Add a per-evaluator recap box under the totals
  // adminMax already computed above
      const groups = new Map();
      evalRows.forEach((r) => {
        const id = r.idevaluateur != null ? String(r.idevaluateur) : '';
        if (!id) return;
        groups.set(id, (groups.get(id) || 0) + toNum(r.note));
      });
      const names = new Map(evaluateurs.map((e) => [String(e.idevaluteur ?? e.idevaluateur ?? e.id_evaluateur ?? ''), (e.prenom || '').trim() || `#${e.idevaluateur}`]));

      const boxH = Math.max(60, 26 + 16 * Math.max(1, groups.size));
      ensureSpace(boxH + 10);
      doc.setDrawColor(...BORDER_CLR);
      doc.setFillColor(250, 251, 252);
      doc.roundedRect(MARGIN_X, y, CONTENT_W, boxH, 8, 8, "F");
      doc.roundedRect(MARGIN_X, y, CONTENT_W, boxH, 8, 8, "S");
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(11);
      doc.text("Récapitulatif par évaluateur", MARGIN_X + 12, y + 18);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(31, 41, 55);
      let ry = y + 38;
      if (groups.size === 0) {
        doc.text("Aucune note saisie par évaluateur.", MARGIN_X + 16, ry);
      } else {
        Array.from(groups.entries()).forEach(([id, total]) => {
          const pct = adminMax > 0 ? Math.round((total / adminMax) * 100) : 0;
          const label = `${names.get(id) || `#${id}`}: ${total}/${adminMax} (${pct}%)`;
          doc.text(label, MARGIN_X + 16, ry);
          ry += 14;
        });
      }
      y += boxH + 12;
    };

    // Render
    drawHeader();

    // Meta grid (General)
    sectionHeader("Informations Projet");
    metaGrid([
      ["Titre", p.titre_projet || `Projet #${p.idprojet}`],
      ["Compétition", rel.competitionTitle],
      ["Date début", p.date_debut_projet],
      ["Date fin", p.date_fin_projet],
      ["Domaine", p.Domaine],
      ["Sous Domaine", p.Sous_Domaine],
      ["Montant Global", fmtNumberLocal(p.Montant_global)],
      ["Subvention Demandée", fmtNumberLocal(p.Montant_subvention)],
    ]);

    // Long scientific content
    const sciBlocks = [
      ["Résumé Français", p.Resume_francais],
      ["Résumé Anglais", p.Resume_anglais],
      ["Introduction du projet", p.Introduction_projet],
      ["Objectif général & Objectifs spécifiques", p.Objectif_general_specifiques],
      ["Méthodologie proposée", p.Methodologie_proposee],
      ["Résultats attendus", p.resultats_attendus],
      ["Considérations éthiques", p.considerations_ethiques],
    ].filter(([, v]) => v && String(v).trim());
    if (sciBlocks.length) {
      sectionHeader("Contenu Scientifique");
      sciBlocks.forEach(([lbl, val]) => {
        ensureSpace(80);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(ACCENT.r, ACCENT.g, ACCENT.b);
        doc.text(safeText(lbl), MARGIN_X, y);
        y += 14;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(40, 40, 40);
        paragraph(String(val));
      });
    }

    // Équipe (list)
    sectionHeader("Équipe");
    if (!equipes.length) {
      doc.setTextColor(120);
      doc.setFontSize(11);
      doc.text(safeText("Aucune donnée d'équipe."), MARGIN_X, y + 14);
      y += 28;
    } else {
      equipes.forEach((t, idx) => {
        ensureSpace(40);
        if (idx % 2 === 1) {
          doc.setFillColor(248, 250, 252);
          doc.rect(MARGIN_X, y - 6, CONTENT_W, 24, "F");
        }
        doc.setTextColor(17, 24, 39);
        doc.setFont("helvetica", "bold");
        doc.text(safeText(t.nom_equipe || "—"), MARGIN_X, y + 12);
        doc.setFont("helvetica", "normal");
        y += 22;
      });
    }

    // Membres de l'équipe (from Node by id_equipe or backend fallback)
    if (membresRows.length) {
      sectionHeader("Membres de l'équipe");
      membresRows.forEach((m, idx) => {
        // Extract with robust fallbacks
        const nom = m.nom_membre ?? m.Nom_membre ?? m.nom ?? "";
        const prenom = m.prenom_membre ?? m.Prenom_membre ?? m.prenom ?? "";
        const civility = m.civility_membre ?? m.Civility_membre ?? m.civilite_membre ?? "";
        const specialite = m.specialite_membre ?? m.Specialite_membre ?? "";
        const roleEquipe = m.role_dans_lequipe ?? m.Role_dans_lequipe ?? m.role ?? "";
        const titre = m.titre_membre ?? m.Titre_membre ?? "";
        const structure = m.structure_membre ?? m.Structure_membre ?? "";
        const contribution = m.contribution_projet_membre ?? m.Contribution_projet_membre ?? "";
        const temps = m.temps_de_travail ?? m.temps_travail ?? "";

        const fullName = `${prenom} ${nom}`.trim() || "-";

        // Build info line + wrapped lines
        const infoParts = [];
        if (civility) infoParts.push(`Civilité: ${civility}`);
        if (titre) infoParts.push(`Titre: ${titre}`);
        if (specialite) infoParts.push(`Spécialité: ${specialite}`);
        if (structure) infoParts.push(`Structure: ${structure}`);
        if (roleEquipe) infoParts.push(`Rôle: ${roleEquipe}`);
        if (temps) infoParts.push(`Temps: ${temps}`);
        const infoText = infoParts.join("  •  ");
        const infoLines = infoText ? doc.splitTextToSize(safeText(infoText), CONTENT_W - 24) : [];

        const contribText = contribution && String(contribution).trim()
          ? `Contribution: ${contribution}`
          : "";
        const contribLines = contribText ? doc.splitTextToSize(safeText(contribText), CONTENT_W - 24) : [];

        // Compute dynamic box height
        const lineH = 14;
        const nameBlock = 18; // name line height
        const infoBlock = infoLines.length * lineH; // may be 0
        const contribBlock = contribLines.length ? (6 + contribLines.length * lineH) : 0; // 6px gap before contrib
        const boxHeight = nameBlock + infoBlock + contribBlock;

        // Ensure room and draw optional zebra background with correct height
        ensureSpace(boxHeight + 20);
        if (idx % 2 === 1) {
          doc.setFillColor(248, 250, 252);
          doc.rect(MARGIN_X, y - 8, CONTENT_W, boxHeight + 18, "F");
        }

        // Render
        doc.setTextColor(17, 24, 39);

        // Line 1: Full name
        doc.setFont("helvetica", "bold");
        doc.text(safeText(fullName), MARGIN_X + 12, y + 12);

        // Line 2+: Info lines
        let cy = y + 28;
        doc.setFont("helvetica", "normal");
        if (infoLines.length) {
          infoLines.forEach((ln, i) => doc.text(ln, MARGIN_X + 12, cy + i * lineH));
          cy += infoLines.length * lineH;
        }

        // Contribution lines
        if (contribLines.length) {
          cy += 6; // small gap before contribution
          contribLines.forEach((ln, i) => doc.text(ln, MARGIN_X + 12, cy + i * lineH));
          cy += contribLines.length * lineH;
        }

        // Advance Y by computed height + a small gap
        y += boxHeight + 6;
      });
      y += 6;
    }

    // Planning (nom_Planning, Description_Planning, debut_planning, fin_planning, nbr_jour_planning)
    if (planningRows.length) {
      sectionHeader("Planning");
      planningRows.forEach((r, idx) => {
        ensureSpace(70);
        if (idx % 2 === 1) {
          doc.setFillColor(248, 250, 252);
          doc.rect(MARGIN_X, y - 6, CONTENT_W, 34, "F");
        }
        doc.setTextColor(17, 24, 39);
        doc.setFont("helvetica", "bold");
        doc.text(safeText(r.nom_Planning || "—"), MARGIN_X + 12, y + 12);
        doc.setFont("helvetica", "normal");
        doc.text(
          safeText(r.Description_Planning || "—"),
          MARGIN_X + 200,
          y + 12,
          { maxWidth: PAGE_W - MARGIN_X - (MARGIN_X + 200) }
        );
        y += 16;
        const d1 = formatDate(r.debut_planning || "");
        const d2 = formatDate(r.fin_planning || "");
        const nj = r.nbr_jour_planning || "—";
        doc.text(
          safeText(`Début: ${d1 || "—"}  •  Fin: ${d2 || "—"}  •  Jours: ${nj}`),
          MARGIN_X + 12,
          y + 12
        );
        y += 20;
      });
      y += 8;
    }

    // Tâches (nom_tache, description_tache, debut_tache, fin_tache, nbr_jour_tache)
    if (tacheRows.length) {
      sectionHeader("Tâches");
      tacheRows.forEach((r, idx) => {
        ensureSpace(70);
        if (idx % 2 === 1) {
          doc.setFillColor(248, 250, 252);
          doc.rect(MARGIN_X, y - 6, CONTENT_W, 34, "F");
        }
        doc.setTextColor(17, 24, 39);
        doc.setFont("helvetica", "bold");
        doc.text(safeText(r.nom_tache || "—"), MARGIN_X + 12, y + 12);
        doc.setFont("helvetica", "normal");
        doc.text(
          safeText(r.description_tache || "—"),
          MARGIN_X + 200,
          y + 12,
          { maxWidth: PAGE_W - MARGIN_X - (MARGIN_X + 200) }
        );
        y += 16;
        const d1 = formatDate(r.debut_tache || "");
        const d2 = formatDate(r.fin_tache || "");
        const nj = r.nbr_jour_tache || "—";
        doc.text(
          safeText(`Début: ${d1 || "—"}  •  Fin: ${d2 || "—"}  •  Jours: ${nj}`),
          MARGIN_X + 12,
          y + 12
        );
        y += 20;
      });
      y += 8;
    }

    // Budget - Paramètres (appellation, date_creation)
    if (budgetParams.length) {
      sectionHeader("Budget - Paramètres");
      budgetParams.forEach((r, idx) => {
        ensureSpace(36);
        if (idx % 2 === 1) {
          doc.setFillColor(248, 250, 252);
          doc.rect(MARGIN_X, y - 4, CONTENT_W, 24, "F");
        }
        doc.setTextColor(17, 24, 39);
        doc.text(safeText(r.appellation || "—"), MARGIN_X + 12, y + 12);
        const dc = formatDate(r.date_creation || "");
        doc.text(safeText(dc || "—"), PAGE_W - MARGIN_X - 180, y + 12);
        y += 20;
      });
      y += 8;
    }

    // Budget du Projet (table)
    sectionHeader("Budget du Projet");
    if (!budgets.length) {
      doc.setTextColor(120);
      doc.setFontSize(11);
      doc.text(safeText("Aucune donnée budgétaire."), MARGIN_X, y + 14);
      y += 28;
    } else {
      drawBudgetTable(budgets);
    }

    // Comité (titre_comite, date_creation)
    if (comiteRows.length) {
      sectionHeader("Comité");
      comiteRows.forEach((r, idx) => {
        ensureSpace(30);
        if (idx % 2 === 1) {
          doc.setFillColor(248, 250, 252);
          doc.rect(MARGIN_X, y - 4, CONTENT_W, 22, "F");
        }
        doc.setTextColor(17, 24, 39);
        doc.text(safeText(r.titre_comite || "—"), MARGIN_X + 12, y + 12);
        const dc = formatDate(r.date_creation || "");
        doc.text(safeText(dc || "—"), PAGE_W - MARGIN_X - 180, y + 12);
        y += 18;
      });
      y += 8;
    }

    // (Évaluateurs moved to the end to be the last category)

    // Évaluation: render as table with totals (evaluator/admin)
    if (evaluationRows.length || criteresRows.length || sousCritRows.length) {
      const gridTitle = (evaluationRows[0] && (evaluationRows[0].Titre_evaluation || evaluationRows[0].titre_evaluation || evaluationRows[0].title)) || "Grille d'évaluation";
      sectionHeader(`Évaluation - ${gridTitle}`);
      drawEvaluationTable(criteresRows.length ? criteresRows : [], sousCritRows.length ? sousCritRows : [], projetEvalRows.length ? projetEvalRows : []);
    }

    // Detailed evaluator entries table
    if ((projetEvalRows.length)) {
      sectionHeader("Évaluations détaillées");
      drawEvaluatorDetailTable(evaluateursRows, projetEvalRows, criteresRows, sousCritRows);
    }

    // Évaluateurs: render as table (last category)
    if (evaluateursRows.length || projetEvalRows.length) {
      sectionHeader("Évaluateurs");
      const adminMax = sousCritRows.reduce((s, r) => s + (parseFloat(r.note) || 0), 0);
      drawEvaluateursTable(evaluateursRows, projetEvalRows, adminMax);
    }

    // Footer and save
    const now = new Date().toLocaleString("fr-FR");
    addFooter();
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `Projet_${p.idprojet || "export"}_${ts}.pdf`;
    console.log("[PDF] Saving client PDF:", filename, { budgetRows: budgets.length, equipeRows: equipes.length });
    doc.save(filename);
    alert("PDF client généré: " + filename);
  };

  // Force client PDF only (no server fallback)
  const handleDownloadPDF = async (project) => {
    console.log("[PDF] Click download for project:", project?.idprojet);
    try {
      await generateProjectPdf(project);
    } catch (err) {
      console.error("[PDF] Client generation failed:", err);
      alert("La génération du PDF côté client a échoué. Voir la console pour plus de détails.");
      // Intentionally no server fallback here to avoid opening pdfDownload.php
    }
  };

  if (loading) return <p>Chargement des détails du projet...</p>;
  if (error) return <p>Erreur : {error}</p>;

  return (
    <div className="project-soumi-container">
      <AdminSidebar />

      <div className="project-soumi-content admin-content">
        <div className="page-container">
          {/* New page header with search */}
          <div className="page-header">
            <div className="page-titles">
              <h2 className="title-soumi">Projets soumis</h2>
              <p className="subtitle-soumi">Aperçu des projets et export PDF</p>
            </div>
            <div className="toolbar">
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="search-input"
                placeholder="Rechercher par titre, chercheur, domaine, équipe..."
                aria-label="Rechercher"
              />
            </div>
          </div>

          {filteredProjects.length > 0 ? (
            <div className="table-card">
              <div className="table-wrap">
                <table className="project-soumi-table">
                  <thead>
                    <tr>
                      <th>N de Projet</th>
                      <th>Titre</th>
                      <th>Chercheur</th>
                      <th>Date de début</th>
                      <th>Date de fin</th>
                      <th>Domaine</th>
                      <th>BUDGET</th>
                      <th>Montant du budget</th>
                      <th>Équipe</th>
                      <th className="cell-action-header">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((project) => (
                      <tr key={project.idprojet}>
                        <td className="cell-id">{clean(project.idprojet)}</td>
                        <td className="cell-title" title={clean(project.titre_projet)}>
                          {clean(project.titre_projet)}
                        </td>
                        <td className="cell-person" title={clean(project.chercheur_nom)}>
                          {clean(project.chercheur_nom)}
                        </td>
                        <td>{printDate(project.date_debut_projet)}</td>
                        <td>{printDate(project.date_fin_projet)}</td>
                        <td>
                          <span className={`badge ${project.Domaine ? "badge-blue" : "badge-gray"}`}>
                            {clean(project.Domaine)}
                          </span>
                        </td>
                        <td className="cell-ellipsis" title={clean(getBudgetAppellation(project))}>
                          {clean(getBudgetAppellation(project))}
                        </td>
                        <td className="cell-money">{printMoney(project.valeur_montant)}</td>
                        <td>
                          <span className={`badge ${project.nom_equipe ? "badge-green" : "badge-gray"}`}>
                            {clean(project.nom_equipe)}
                          </span>
                        </td>
                        <td className="cell-action">
                          <button
                            type="button"
                            onClick={() => handleDownloadPDF(project)}
                            className="download-button btn-primary btn-sm"
                            title="Exporter en PDF (client)"
                          >
                            Télécharger
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-meta">
                <span>{filteredProjects.length} projets</span>
                {q && <span className="muted"> • filtré par “{q}”</span>}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-card">
                <h3>Aucun résultat</h3>
                <p>Essayez un autre mot-clé ou effacez le filtre.</p>
                <button type="button" className="btn-secondary" onClick={() => setQ("")}>
                  Effacer le filtre
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Project_Soumi;
